/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { action, set } from '@ember/object';
import { LAST_30_DAYS_RANGE_PRESET } from 'cio/utils/workflow-metrics';
import { Channel, Measure, Periods, PERIOD_KEYS } from 'cio/utils/metrics/common';
import { tracked } from '@glimmer/tracking';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { Grouping } from 'cio/utils/metrics/resolution';
import { isEmpty } from '@ember/utils';
import moment from 'moment-timezone';
import { SortDirection } from 'cio/utils/sort';
import { JourneyMetrics, JourneyMetricsMeasure } from 'cio/utils/journey-metrics';
import { htmlSafe } from '@ember/template';
import { hash } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import MetricsService from 'cio/services/metrics';
import { Metric } from 'cio/utils/metrics/models/metric';
import AnalyticsService from 'cio/services/analytics';
import SettingsService from 'cio/services/settings';

interface CampaignMetricsPageArgs {
  campaign: CampaignModel;

  startTime: string;
  endTime: string;
  onTimeRangeChange: (start: string, end: string) => unknown;

  period: Periods;
  onPeriodChange: (period: Periods) => unknown;

  grouping: Grouping;
  onGroupingChange: (grouping: Grouping, trackEvent?: boolean) => unknown;

  unit: 'count' | 'percentage';
  onUnitChange: (unit: 'count' | 'percentage') => unknown;

  graphTab: 'delivery' | 'journey';
  onGraphTabChange: (tab: 'delivery' | 'percentage') => unknown;

  performanceDeliveryChartType: 'individual' | 'aggregate';
  onPerformanceDeliveryChartTypeChange: (type: 'individual' | 'aggregate') => unknown;

  performanceDeliveryChannel: Channel;
  onPerformanceDeliveryChannelChange: (channel: Channel) => unknown;

  performanceDeliveryIndividualMeasure: Measure;
  onPerformanceDeliveryIndividualMeasureChange: (measure: Measure) => unknown;

  performanceDeliveryMeasures: Measure[];
  onPerformanceDeliveryMeasuresChange: (measures: Measure[]) => unknown;

  journeysMeasures: JourneyMetricsMeasure[];
  onJourneysMeasuresChange: (measures: JourneyMetricsMeasure[], track?: boolean) => unknown;

  messageMetricsChannels: Channel[];
  onMessageMetricsChannelsChange: (channels: Channel[]) => unknown;

  messageMetricsSortBy: 'action_name' | Measure;
  messageMetricsSortDirection: SortDirection;
  onMessageMetricsSortChange: (sortBy: 'action_name' | Measure, sortDirection: SortDirection) => unknown;

  messageMetricsColumns: Measure[];
  onMessageMetricsColumnsChange: (measures: Measure[], track?: boolean) => unknown;
}

export default class CampaignMetricsPage extends Component<CampaignMetricsPageArgs> {
  @service analytics!: AnalyticsService;
  @service metrics!: MetricsService;
  @service settings!: SettingsService;

  @tracked timeRange = LAST_30_DAYS_RANGE_PRESET.getRange();

  constructor(owner: unknown, args: CampaignMetricsPageArgs) {
    super(owner, args);

    if (!isEmpty(args.startTime) && !isEmpty(args.endTime)) {
      this.timeRange = {
        start: moment(args.startTime),
        end: moment(args.endTime),
      };
    }
  }

  @action
  onRangeSelect(range: DateRange, period: Periods) {
    this.timeRange = range;

    this.args.onPeriodChange(period);

    if (this.args.grouping !== PERIOD_KEYS[period]) {
      this.args.onGroupingChange(PERIOD_KEYS[period], false);
    }

    this.args.onTimeRangeChange(range.start.format('YYYY-MM-DD'), range.end.format('YYYY-MM-DD'));

    this.fetchDeliveryAndMessageMetrics.perform();
    this.fetchJourneysMetrics.perform();
  }

  @action
  onGroupingSelect(grouping: Grouping) {
    this.args.onGroupingChange(grouping);

    this.fetchDeliveryAndMessageMetrics.perform();
    this.fetchJourneysMetrics.perform();
  }

  // Used to add a scroll-margin-top style to page section links, so that the
  // anchor links add a margin to the scroll bar
  // https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-margin-top
  // We calculate it through JS rather than hardcoding because the masq bar and
  // other banners might affect the margins
  @tracked containerOffset = 0;

  get containerOffsetStyle() {
    if (this.containerOffset === 0) {
      return null;
    }

    return htmlSafe(`scroll-margin-top: ${this.containerOffset}px;`);
  }

  get hasAnyInAppMessageAction() {
    return this.args.campaign.workflowActions.some((action) => action.isInApp);
  }

  @action
  scrollOffset() {
    let pageContainer = document.querySelector('.campaign-metrics-page-controls-section');

    if (pageContainer == null) {
      return;
    }

    // https://stackoverflow.com/questions/9880472/determine-distance-from-the-top-of-a-div-to-top-of-window-with-javascript#comment91212465_35571753
    this.containerOffset = window.scrollY + pageContainer.getBoundingClientRect().bottom + 10;
  }

  // The message metrics card is the last card on the page, and the only one
  // with a variable height. As such, it has an effect on the overall page
  // length.
  // After the first load, we want to call maybeScrollToContainer to scroll the
  // page to the appropriate section, if an # id hash is present in the link.
  // We need to call this scroll manually, because the browser won't jump to
  // the right section in a single-page application (because the element in
  // question isn't present at initial page load)
  messageMetricsLoadCount = 0;

  @action
  afterMessageMetricsLoaded() {
    this.messageMetricsLoadCount = +1;
    if (this.messageMetricsLoadCount > 1) {
      return;
    }
    this.maybeScrollToContainer();
    // scheduleOnce('afterRender', this, this.maybeScrollToContainer);
  }

  @action
  maybeScrollToContainer() {
    let { hash } = window.location;

    if (isEmpty(hash)) {
      return;
    }

    let containerToScrollTo = document.querySelector(hash);

    if (containerToScrollTo == null) {
      return;
    }

    window.scrollTo(0, window.pageYOffset + containerToScrollTo.getBoundingClientRect().top - this.containerOffset);
  }

  @tracked channelMetrics: Partial<Record<Channel, Metric>> = {};
  @tracked actionDeliveryMetrics: Record<string, Metric> = {};

  fetchDeliveryAndMessageMetrics = task({ keepLatest: true }, async () => {
    let { period } = this.args;

    if (period === 'week') {
      period = Periods.CUSTOM;
    }

    let metrics = await hash({
      campaignChannelMetrics: this.metrics.fetchCampaignChannelDeliveryMetrics(
        this.args.campaign.id,
        period,
        this.timeRange,
      ),
      actionDeliveryMetrics: this.metrics.fetchCampaignActionDeliveryMetrics(
        this.args.campaign.id,
        period,
        this.timeRange,
      ),
    });

    Object.values(metrics.campaignChannelMetrics).forEach((m: Metric) => {
      if (m.actionType === 'webhook_action') {
        if (this.args.campaign.messagingActions.some((a) => a.type === 'webhook_action' && a.canConvert)) {
          set(m, 'canConvert', true);
        }
      }
      set(m, 'grouping', this.args.grouping);
    });
    Object.values(metrics.actionDeliveryMetrics).forEach((m: Metric) => set(m, 'grouping', this.args.grouping));

    this.channelMetrics = metrics.campaignChannelMetrics;
    this.actionDeliveryMetrics = metrics.actionDeliveryMetrics;

    this.afterMessageMetricsLoaded();
  });

  @tracked journeysMetrics?: JourneyMetrics;

  fetchJourneysMetrics = task({ keepLatest: true }, async () => {
    let journeysMetrics = await this.metrics.fetchJourneyMetrics(this.args.campaign.id, this.args.period, {
      start: moment.tz(
        this.timeRange.start.format('YYYY-MM-DD'),
        this.settings.environmentSettings.journeyMetricsTimezone,
      ),
      end: moment.tz(this.timeRange.end.format('YYYY-MM-DD'), this.settings.environmentSettings.journeyMetricsTimezone),
    });

    set(journeysMetrics, 'grouping', this.args.grouping);
    this.journeysMetrics = journeysMetrics;
  });

  @action onTrackCopyLink() {
    this.analytics.track('campaign', 'copied_metrics_link', {
      model: this.args.campaign,
    });
  }

  @action onTrackCopyTrackedResponse() {
    this.analytics.track('campaign', 'copied_metrics_tracked_response', {
      model: this.args.campaign,
    });
  }

  get trackedMetricsContainerClasses() {
    return this.hasAnyInAppMessageAction ? 'flex space-x-3' : '';
  }

  get trackedMetricsCardClasses() {
    return this.hasAnyInAppMessageAction ? 'w-1/2' : '';
  }
}
